import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import { useMutation } from '@apollo/react-hooks'
import loadable from '@loadable/component'
import dynamic from 'next/dynamic'
import { vsprintf } from 'sprintf-js'

import { useProductOptionsTransformer } from '@thg-commerce/enterprise-components'
import { FulfilmentMethodIconsRenderer } from '@thg-commerce/enterprise-components/FulfilmentMethods/FulfilmentMethodIconsRenderer'
import {
  PersonalisationForm,
  PersonalisationToggle,
} from '@thg-commerce/enterprise-components/Personalisation'
import { PersonalisationData } from '@thg-commerce/enterprise-components/Personalisation/PersonalisationForm'
import { usePersonalisationToggleContext } from '@thg-commerce/enterprise-components/Personalisation/PersonalisationToggleContext'
import { useQuickBuyClickHandler } from '@thg-commerce/enterprise-components/Product/src/ProductBlock/hooks'
import { WishlistButtonType } from '@thg-commerce/enterprise-components/Product/src/WishlistButton/WishlistButton'
import { SubscriptionPaymentType } from '@thg-commerce/enterprise-components/ProductVariations'
import { PurchaseOptions } from '@thg-commerce/enterprise-components/PurchaseOptions'
import { VariantFields as TransformerVariant } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import {
  BasketContext,
  EnterpriseContext,
  i18n,
  Routes,
  useAddToBasket,
  useEnterpriseContext,
  useFormattableI18nProperty,
  useLogger,
  useRequestConfig,
  useSessionSettings,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { InteractionLocation } from '@thg-commerce/enterprise-core/src/Basket'
import { CartInteractionEventType } from '@thg-commerce/enterprise-core/src/Basket/hooks/useAddToBasket/initialAddEvent'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { getMaxQuantity } from '@thg-commerce/enterprise-core/src/Basket/QuickBuyModalPresenter/utils'
import { MutationAddPersonalisedProductToBasketArgs } from '@thg-commerce/enterprise-graphql/horizon-schema'
import {
  getColumboEventData,
  useBackendEventNormaliser,
} from '@thg-commerce/enterprise-metrics'
import { SelectedOptions } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import {
  Country,
  Currency,
  Feature,
  FulfilmentMethod,
  InStockLocation,
  MutationAddProductToBasketWithSubscriptionContractArgs,
  SubscriptionContract,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  ChoiceMap,
  VariantChoices,
} from '@thg-commerce/enterprise-network/src/transformers/product/options'
import {
  ProductSummaryPriceDisplayStyle,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { ChoiceSelectedVariantMap } from '@thg-commerce/enterprise-product-options/src/transformers/productOptionsMap'
import { VariantOptions } from '@thg-commerce/enterprise-product-options/src/types'
import { spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import {
  PlatformMessage,
  Price,
  QuantitySelector,
  QuantitySelectorProps,
  SafeHtml,
  Separator,
} from '@thg-commerce/gravity-elements'
import { SwatchShape } from '@thg-commerce/gravity-elements/Swatch/types'
import { ProductOptions } from '@thg-commerce/gravity-patterns'

import { FreeDeliveryIndicator } from '../../../components'
import { AddToBasketButton } from '../../../components/AddToBasketButton/AddToBasketButton'
import { PriceContainer } from '../../../components/ProductSummary/styles'
import { PushNotifyWhenInStock } from '../../../components/PushNotifyWhenInStock/PushNotifyWhenInStock'
import { useProductPage } from '../../../context'
import { AddPersonalisedProductToBasket as ADD_TO_BASKET_PERSONALISED_PRODUCTS } from '../../../graphql/AddPersonalisedProductToBasket.graphql'
import { AddProductToBasketWithSubscriptionContract as ADD_TO_BASKET_WITH_CONTRACT } from '../../../graphql/AddProductToBasketWithSubscriptionContract.graphql'
import { WishlistButtonRendererProps } from '../../WishlistButtonRenderer'

import {
  AddToBasketContainer,
  QuantityPlatformMessage,
  QuantitySelectorContainer,
  QuantityText,
  StyledButtonContainer,
  WishlistButtonWrapper,
} from './styles'

const SESSION_STORAGE_KEY = 'personalisationToggleState'

const ClickAndCollectButtonRendererComponent = loadable(
  () =>
    import('../../ClickAndCollectButtonRenderer').then(
      (mod) => mod.ClickAndCollectButtonRenderer,
    ),
  {
    ssr: true,
  },
)

const ClickAndCollectButtonRenderer = withHydrationOnDemand({
  on: [['visible']],
})(ClickAndCollectButtonRendererComponent)

const WishlistButtonRenderer = dynamic<WishlistButtonRendererProps>(() =>
  import('../../WishlistButtonRenderer').then(
    (mod) => mod.WishlistButtonRenderer,
  ),
)

const OutOfStockText = styled(SafeHtml)`
  ${Text('bodyText', 'default')}
  margin-bottom: ${spacing(2)};
`

export const SubscriptionInfoMessage = styled(PlatformMessage)`
  margin-bottom: ${spacing(2)};
`

export interface ProductOptionSelectorProps {
  i18nText: {
    quantitySelector: {
      increaseButtonLabel: string
      decreaseButtonLabel: string
      quantityLabel: string
    }
    addToBasketButton: {
      addToBasketText: string
      outOfStockText: string
      preorderNowText: string
      personaliseProductText: string
    }
    clickAndCollect: {
      clickAndCollectButtonText: string
      clickAndCollectLinkText: string
      clickAndCollectCheckStoreStockText: string
    }
  }
  options: VariantChoices
  enableQuantity: boolean
  forceDropDowns?: boolean
  swatchShape?: SwatchShape
  showSwatches?: boolean
  notifyWhenInStock?: boolean
  labelCustomComponent?: React.ReactNode
  enableMobilePDPStickyAddToBasket?: boolean
  isFirstProductRecommendation?: boolean
  enablePriceOnMobilePDPStickyAddToBasket?: boolean
  personalisationImages?: ChoiceSelectedVariantMap | VariantOptions
  enableSubscribeAndSaveInlinePricing?: boolean
  setActiveTabIndex: (state: number) => void
  activeTabIndex: number
}

type AddBasketWithContractType = {
  addProductToBasketWithSubscriptionContract: {
    id: string
  }
}

type AddBasketPersonalisedProduct = {
  addPersonalisedProductToBasket: {
    id: string
  }
}

const INITIAL_QUANTITY = 1
enum OptionType {
  Size = 'Size',
}
const INVALID_CONTRACT_ID = '15'

const getAddToBasketText = ({
  inStock,
  addToBasketButton,
  preorder,
  personalisedProduct,
}: {
  inStock?: boolean
  addToBasketButton: {
    addToBasketText: string
    outOfStockText: string
    preorderNowText: string
    personaliseProductText: string
  }
  preorder?: boolean
  personalisedProduct?: boolean
}) => {
  if (!inStock) {
    return addToBasketButton.outOfStockText
  }

  if (personalisedProduct) {
    return addToBasketButton.personaliseProductText
  }

  return preorder
    ? addToBasketButton.preorderNowText
    : addToBasketButton.addToBasketText
}

const isPersonalisationEmpty = (
  selectedVariant: ProductVariantWithDescription,
  personalisationData: PersonalisationData,
) =>
  selectedVariant?.personalisationFields &&
  selectedVariant.personalisationFields.length >
    Object.keys(personalisationData).length
    ? true
    : Object.values(personalisationData).includes('')

export const ProductOptionSelector = <
  Variant extends TransformerVariant & {
    notifyWhenInStockEnabled?: boolean | null
    disableColorDropdown?: boolean
  }
>(
  props: ProductOptionSelectorProps & {
    variants: Variant[]
    selectedVariant?: Variant
    defaultVariant?: Variant
    variantChangedCallback: (variant: Variant) => void
    selectedOptions?: SelectedOptions
    setSelectedOptionsCallback?: (selectedOptions: SelectedOptions) => void
    choiceMap?: ChoiceMap
    parentSku?: string
    disableColorDropdown?: boolean
  },
) => {
  const productOptionsTransformer = useProductOptionsTransformer<Variant>()
  const transmit = useTransmit()
  const normaliseBackendEvent = useBackendEventNormaliser()
  const EnterpriseCtx = React.useContext(EnterpriseContext)
  const {
    domain,
    defaultLocale,
    subsite,
    siteId,
    channel,
  } = useSiteDefinition()
  const { isMobileApp } = useRequestConfig()
  const { currency, shippingDestination } = useSessionSettings()
  const {
    enablePushNotifyWhenInStock,
    enablePreselectedProductVariant,
    enableClickAndCollect,
    showSavingsAmountOnSubscriptionCard,
    enableProductQuantityInStockAvailability,
    papMessageTopPosition,
    displayPriceAboveQuantity,
    showOOSMessagePDP,
    enableMobilePDPStickyAddToBasket,
    enableQuickbuyOnlyOnMobile,
  } = useSiteConfig()
  const {
    product,
    selectedVariant,
    setSelectedOptions,
    selectedOptions,
  } = useProductPage()
  const {
    isPersonalisationActive,
    variantSku,
    setVariantSku,
    personalisationFields,
    setPersonalisationFields,
    setPersonalisationImagesContext,
  } = usePersonalisationToggleContext()
  const { basket } = useBasket()
  const { horizonFeatures, extensionsRef } = useEnterpriseContext()
  const { execute: addToBasket } = useAddToBasket({ forceAddToBasket: true })
  const theme = useTheme()
  const logger = useLogger()
  const [optedInVariants, setOptedInVariants] = React.useState<{
    [key: string]: boolean
  }>({})
  const [displayError, setDisplayError] = React.useState<boolean>(false)

  const [
    activePersonalisationTabIndex,
    setActivePersonalisationTabIndex,
  ] = React.useState<number>(
    theme.pageTheme.productPersonalisation?.showClassicContent ? 0 : 1,
  )

  const isPersonalisedProduct = !!selectedVariant?.personalisationFields?.length

  const [personalisationData, setPersonalisationData] = React.useState<
    PersonalisationData
  >({})

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const stateToStore = {
        isPersonalisationActive,
        variantSku,
        personalisationFields,
      }
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToStore))
    }
  }, [
    isPersonalisedProduct,
    isPersonalisationActive,
    variantSku,
    personalisationFields,
  ])

  const {
    value: [getExtensions],
  } = extensionsRef

  const [selectedFrequencyId, setSelectedFrequencyId] = React.useState<string>(
    INVALID_CONTRACT_ID,
  )

  const basketProductSku = basket?.items.find(
    (item) => item.product.sku === props.selectedVariant?.sku,
  )?.subscriptionContract

  React.useEffect(() => {
    const initialFrequencyId =
      basketProductSku?.id ??
      props.selectedVariant?.subscriptionContracts?.[0]?.id ??
      INVALID_CONTRACT_ID

    setSelectedFrequencyId(initialFrequencyId)
  }, [props.selectedVariant])

  const addToBasketButtonContainerRef = React.useRef<HTMLDivElement>(null)
  const [addToBasketIsSticky, setAddToBasketIsSticky] = React.useState<boolean>(
    false,
  )
  const [hasScrolled, setHasScrolled] = React.useState<boolean>(false)

  React.useEffect(() => {
    const containerRef = addToBasketButtonContainerRef.current

    const observer = new IntersectionObserver(
      ([element], _) => {
        if (element && element.isIntersecting) {
          setAddToBasketIsSticky(false)
        } else {
          setAddToBasketIsSticky(true)
        }
      },
      {
        rootMargin: `0px`,
        threshold: 1,
      },
    )
    containerRef && observer.observe(containerRef)

    return () => {
      containerRef && observer.unobserve(containerRef)
    }
  }, [])

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true)
        window.removeEventListener('scroll', handleScroll)
      }
    }
    if (props.enableMobilePDPStickyAddToBasket) {
      window.addEventListener('scroll', handleScroll)
    }
    return () => window.removeEventListener('scroll', handleScroll)
  })

  const canOptInForCurrentVariant =
    !props.selectedVariant?.inStock &&
    props.selectedVariant?.notifyWhenInStockEnabled

  const showSetupPushNotificationText =
    enablePushNotifyWhenInStock &&
    isMobileApp &&
    product?.hasVariantsWithNotifyWhenInStockEnabled

  const showPushNotificationView = Boolean(
    enablePushNotifyWhenInStock && isMobileApp && canOptInForCurrentVariant,
  )

  const showAddToBasketWishlist =
    theme.pageTheme?.productOptions.addToBasketContainer.addToWishlistButton
      .show

  const wishlistButtonStyle =
    theme.pageTheme?.productOptions.addToBasketContainer.addToWishlistButton
      ?.style

  const clickAndCollectAvailable = Boolean(
    props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
      FulfilmentMethod.CollectInStore,
    ),
  )

  const variantHasHomeDelivery = Boolean(
    props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
      FulfilmentMethod.HomeDelivery,
    ) &&
      props.selectedVariant?.inStockLocations?.includes(
        InStockLocation.Warehouse,
      ),
  )
  const homeDeliveryAvailable = props.selectedVariant
    ?.eligibleForFulfilmentMethods
    ? variantHasHomeDelivery
    : true

  const showQuantity =
    props.enableQuantity &&
    (enableProductQuantityInStockAvailability
      ? homeDeliveryAvailable ||
        clickAndCollectAvailable ||
        props.selectedVariant?.isCheckStock
      : true)

  const isAddToBasketSticky =
    props.enableMobilePDPStickyAddToBasket && addToBasketIsSticky && hasScrolled

  const isInStockCheck =
    showOOSMessagePDP &&
    !props.selectedVariant?.inStock &&
    enablePreselectedProductVariant

  const i18nText = {
    offLabel: i18n('general.off.text'),
    rrpLabel: i18n('general.rrp.text'),
    saveLabel: i18n('general.save.text'),
    quantitySelector: {
      increaseButtonLabel: i18n('general.quantity.increase'),
      decreaseButtonLabel: i18n('general.quantity.decrease'),
      quantityLabel: i18n('general.quantity.text'),
    },
    addToWishListButton: {
      addToWishListText: i18n('product.addtowishlistbutton.addtowishlist.text'),
    },
    addToBasketButton: {
      addToBasketText: i18n('product.optionselector.addtobasket.text'),
      outOfStockText: i18n('product.productoptions.dropdown.unavailable.text'),
      preorderNowText: i18n('listitem.button.preordernow.text'),
      personaliseProductText: i18n(
        'product.optionselector.personalisedproduct.text',
      ),
    },
    wishlistTooltipButton: {
      closeTooltip: i18n('product.addtowishlist.tooltip.button.close.text'),
    },
    wishlistTooltipContent: {
      loginSignupText: i18n('product.addtowishlist.tooltip.loginsignup.text'),
      toUseWishlistText: i18n('product.addtowishlist.tooltip.text'),
    },
    basketQuantity: {
      limit: i18n('product.maxquantity.basket.limit.text'),
      singleItemText: i18n('product.maxquantity.basket.singleitem.text'),
      multipleItemsText: i18n('product.maxquantity.basket.multipleitems.text'),
    },
    outOfStock: {
      outOfStocktext: i18n('product.productoptions.dropdown.unavailable.text'),
      optInText: i18n('product.outofstocknotification.optin.text'),
      optedInText: i18n('product.outofstocknotification.optedin.text'),
    },
    productWithSubscriptionContracts: i18n(
      'product.inbasketwith.subscription.contracts',
    ),
    alreadyInBasketAsSubscription: i18n(
      'product.alreadyaddedinbasket.info.text',
    ),
    missingSizeError: i18n('product.missing.size.error'),
    oneTimePurchaseDiscountText: useFormattableI18nProperty(
      'product.subscription.onetimepurchase.discount.percentage.text',
    ),
    decreaseButtonLabelDynamic: i18n(
      'general.quantity.decrease.dynamic',
      props.selectedVariant?.title,
    ),
    increaseButtonLabelDynamic: i18n(
      'general.quantity.increase.dynamic',
      props.selectedVariant?.title,
    ),
  }
  const fulfilmentMethodIconsProps = {
    enableClickAndCollect,
    inStockLocations: props.selectedVariant?.inStockLocations,
    inStock: Boolean(props.selectedVariant?.inStock),
    isCheckStock: Boolean(props.selectedVariant?.isCheckStock),
    isOrderInStore: Boolean(props.selectedVariant?.isOrderInStore),
    leadTime: props.selectedVariant?.leadTime,
    weightGroups: props.selectedVariant?.weightGroups,
    isBookable: Boolean(props.selectedVariant?.isBookable),
    iconsAvailability: {
      homeDelivery: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.HomeDelivery,
        ),
      ),
      storeAvailable: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.CollectInStore,
        ),
      ),
      storeDelivery: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.DeliverToStore,
        ),
      ),
    },
    i18nText: {
      clickAndCollect: {
        isAvailable: i18n('product.delivery.store.available.text'),
        isNotAvailable: i18n('product.clickandcollect.unavailable.text'),
      },
      homeDelivery: {
        isAvailable: i18n('product.delivery.home.instock.text'),
        isNotAvailable: i18n('product.delivery.home.unavailable.text'),
        isOutOfStock: i18n('product.delivery.home.outofstock.text'),
        datedDelivery: i18n(
          'basket.item.fulfilment.leadtime',
          props.selectedVariant?.leadTime?.toString(),
        ),
        nextDayDelivery: i18n('basket.item.fulfilment.nextdaydelivery'),
        oneManDelivery: i18n(
          'product.item.fulfilment.1man.nextdaydeliveryavailable',
        ),
        outOfGaugeDelivery: i18n(
          'product.item.fulfilment.outofgauge.nameddaydeliveryavailable',
        ),
        dynamicDelivery: i18n(
          `product.item.fulfilment.pdp.${props.selectedVariant?.weightGroups?.[0]?.toLowerCase()}`,
        ),
      },
      storeDelivery: {
        isAvailable: i18n('product.delivery.store.available.text'),
      },
      orderInStore: {
        isAvailable: i18n(
          'product.item.fulfilment.orderinstore.available.text',
        ),
        message: i18n('product.item.fulfilment.orderinstore.available.message'),
      },
      pdpDeliveryMessage: {
        homeDeliveryMsg1Man: i18n('product.item.fulfilment.1man.secondmessage'),
        homeDeliveryMsgOutOfGauge: i18n(
          'product.item.fulfilment.outofgauge.secondmessage',
        ),
        storeMsg: i18n('product.storepickup.cost'),
      },
    },
    missingSizeError: i18n('product.missing.size.error'),
  }

  const [selectedQuantity, setSelectedQuantity] = React.useState(
    INITIAL_QUANTITY,
  )
  const notificationOptInText =
    props.selectedVariant && optedInVariants[props.selectedVariant.sku]
      ? i18nText.outOfStock.optedInText
      : vsprintf('<strong>%s%s</strong> %s', [
          i18nText.outOfStock.outOfStocktext,
          !props.selectedVariant?.inStock ? ' -' : '?',
          i18nText.outOfStock.optInText,
        ])

  const updateProductVariants = (params: {
    sku: string
    isOptedIn: boolean
  }) => {
    const nextState = { ...optedInVariants }
    nextState[params.sku] = params.isOptedIn
    setOptedInVariants(nextState)
  }

  const { presentAddedToBasketModal, basketIdRef } = React.useContext(
    BasketContext,
  )

  const {
    variants,
    defaultVariant,
    variantChangedCallback,
    setSelectedOptionsCallback,
    selectedVariant: memoisedSelectedVariant,
    selectedOptions: memoisedSelectedOptions,
  } = props

  const productOptions = React.useMemo(() => {
    const updateSelectedVariant = (variant: Variant) => {
      variantChangedCallback(variant)
    }

    return productOptionsTransformer({
      variants,
      defaultVariant,
      options: [],
      selectedOptions: memoisedSelectedOptions,
      selectedVariant: memoisedSelectedVariant,
      selectedOptionsChangedCallback: setSelectedOptionsCallback,
      variantChangedCallback: updateSelectedVariant,
    })
  }, [
    memoisedSelectedOptions,
    productOptionsTransformer,
    defaultVariant,
    memoisedSelectedVariant,
    setSelectedOptionsCallback,
    variants,
    variantChangedCallback,
  ])

  const hasProductOptions = !!productOptions.options.length

  const { maxQuantity, quantityInBasket } = getMaxQuantity(
    basket,
    props.selectedVariant,
  )

  const maxQuantityBasketItemMessageText = vsprintf(
    quantityInBasket > 1
      ? i18nText.basketQuantity.multipleItemsText
      : i18nText.basketQuantity.singleItemText,
    [props.selectedVariant?.maxPerOrder || 5000, quantityInBasket],
  )

  const quantitySelectorProps: QuantitySelectorProps = {
    i18nText: {
      increaseButtonLabel: i18nText.quantitySelector.increaseButtonLabel,
      decreaseButtonLabel: i18nText.quantitySelector.decreaseButtonLabel,
      quantityLabel: i18nText.quantitySelector.quantityLabel,
      decreaseButtonLabelDynamic: i18nText.decreaseButtonLabelDynamic,
      increaseButtonLabelDynamic: i18nText.increaseButtonLabelDynamic,
    },
    productTitle: props.selectedVariant?.title,
    quantity: selectedQuantity,
    minValue: 1,
    maxValue: maxQuantity,
    editable: true,
    quantityChangedCallback: (quantity) => {
      quantity && setSelectedQuantity(quantity)
    },
  }

  let productOptionVariantSku = ''

  if (props.selectedVariant) {
    productOptionVariantSku = props.selectedVariant.sku
  } else if (!enablePreselectedProductVariant) {
    productOptionVariantSku = '0'
    if (props.selectedOptions) {
      if (Object.keys(props.selectedOptions).length && props.choiceMap) {
        productOptionVariantSku = Object.values(props.selectedOptions)[0]
      }
    }
  } else if (props.defaultVariant) {
    productOptionVariantSku = props.defaultVariant.sku
  }

  const propertyArgs = React.useMemo(
    () => ({
      defaultLocale,
      subsite,
      siteId,
      channel,
      customerLocation: EnterpriseCtx.currentLocation,
    }),
    [defaultLocale, subsite, siteId, channel, EnterpriseCtx.currentLocation],
  )

  const onStickyAddToBasketClick = React.useCallback(() => {
    const html_element = variantHasHomeDelivery
      ? 'sticky-delivery-button'
      : 'add-to-basket-sticky-bottom'

    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'Accessibility',
          '1': 'Clicked',
          '2': 'Add To Basket',
        },
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: variantHasHomeDelivery
            ? 'sticky-delivery-button'
            : 'productAddToBasket',
          contents: [{ html_element }],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }, [
    variantHasHomeDelivery,
    EnterpriseCtx.metricNonce,
    propertyArgs,
    transmit,
  ])

  const onColourSwatchClick = () => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'Accessibility',
          '1': 'Clicked',
          '2': 'Swatch',
        },
        attributes:
          typeof props.isFirstProductRecommendation === 'undefined'
            ? []
            : props.isFirstProductRecommendation
            ? ['primarySwatch']
            : ['secondarySwatch'],
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: 'shade_selector',
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const onDropdownClick = () => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'Accessibility',
          '1': 'Clicked',
          '2': 'Dropdown',
        },
        attributes:
          typeof props.isFirstProductRecommendation === 'undefined'
            ? []
            : props.isFirstProductRecommendation
            ? ['primaryDropdown']
            : ['secondaryDropdown'],
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: 'product_variations_dropdown',
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const onLinkedSkuOptionChange = (option: string) => {
    if (!product?.linkedOn || !selectedVariant) return

    if (option === product.linkedOn) {
      transmit({
        type: 'columbo',
        payload: getColumboEventData({
          propertyArgs,
          argumentsObj: {
            '0': 'Accessibility',
            '1': 'Clicked',
            '2': 'LinkedSkuOption',
          },
          requestArgs: {
            client_timestamp: Math.round(Date.now() / 1000),
            url: window.location.href,
          },
          eventData: {
            type: 'click',
            subtype: 'linkedSKUOption',
            contents: [],
          },
          nonce: EnterpriseCtx.metricNonce,
        }),
      })
    }
  }

  const hasSizeOptions =
    !enablePreselectedProductVariant &&
    props.options[productOptionVariantSku]?.length > 0 &&
    props.options[productOptionVariantSku].find(
      (item) => item.key === OptionType.Size,
    ) &&
    props.selectedOptions &&
    !(OptionType.Size in props.selectedOptions)

  const quickBuySelectedSku = !enablePreselectedProductVariant
    ? product?.sku
    : props.selectedVariant?.sku
  const quickBuyClickHandler = useQuickBuyClickHandler(
    quickBuySelectedSku || '',
    false,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    enableMobilePDPStickyAddToBasket,
  )

  const quickBuyModal = (event?: React.MouseEvent<Element, MouseEvent>) => {
    if (event) {
      event.stopPropagation()
      quickBuyClickHandler(event)
    }
  }

  const addProductToBasket = React.useCallback(() => {
    if (!props.selectedVariant || hasSizeOptions) {
      hasSizeOptions && setDisplayError(true)
      return
    }
    addToBasket(
      [
        {
          sku: props.selectedVariant.sku.toString(),
          quantity: selectedQuantity,
        },
      ],
      {
        fromRecommendations: false,
        location: InteractionLocation.PDP,
      },
    )
    isAddToBasketSticky && onStickyAddToBasketClick()
  }, [
    props.selectedVariant,
    selectedQuantity,
    isAddToBasketSticky,
    onStickyAddToBasketClick,
    addToBasket,
    hasSizeOptions,
  ])

  const showAddToBasket = props.selectedVariant?.notifyWhenInStockEnabled
    ? props.selectedVariant?.inStock || false
    : true
  const [, setBasketId] = basketIdRef.value

  const personalisationUrl = `https://${domain}${Routes.PersonaliseYourBox}?productId=${props.selectedVariant?.sku}`

  const [personalisationImages, setPersonalisationImages] = React.useState<
    ChoiceSelectedVariantMap | VariantOptions
  >()

  const [addToBaskedWithContracts] = useMutation<
    {
      addProductToBasketWithSubscriptionContract: AddBasketWithContractType
    },
    MutationAddProductToBasketWithSubscriptionContractArgs
  >(ADD_TO_BASKET_WITH_CONTRACT, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      logger.warn(
        `[AddToBasketWithContracts]: Failed to add to basket ${error.message}`,
      )
    },
    onCompleted: (data) => {
      if (props.selectedVariant) {
        const resultExtensions =
          (data as any)?.addProductToBasketWithSubscriptionContract
            ?.extensions || getExtensions()
        const selectedContract = props.selectedVariant?.subscriptionContracts?.find(
          (contract) =>
            parseInt(contract.id, 10) === parseInt(selectedFrequencyId, 10),
        )

        transmit({
          type: 'cart_interaction_event',
          payload: normaliseBackendEvent<CartInteractionEventType>({
            basket,
            eventData: {
              subtype: 'initial_add',
              type: 'cart_interaction_event',
              items: [
                {
                  quantity: selectedQuantity,
                  product_group: {
                    id: props.parentSku
                      ? parseInt(props.parentSku, 10)
                      : parseInt(props.selectedVariant.sku, 10),
                    selected_variant: {
                      price: {
                        currency,
                        value: props.selectedVariant.price?.price.amount
                          ? Number(props.selectedVariant.price?.price.amount)
                          : undefined,
                      },
                      sku: parseInt(props.selectedVariant.sku, 10),
                      selected_subscription: selectedContract
                        ? {
                            plan_id: Number(selectedContract.id),
                            initial_discount_percentage:
                              selectedContract.initialDiscountPercentage,
                            recurring_discount_percentage:
                              selectedContract.recurringDiscountPercentage,
                            frequency_unit:
                              selectedContract.frequencyDuration.unit,
                            frequency_month: selectedContract.frequencyDuration.duration.toString(),
                            recommended_frequency: selectedContract.recommended,
                          }
                        : undefined,
                    },
                    total_variants: product?.variants.length,
                  },
                },
              ],
            },
            experiments: resultExtensions?.experiments,
            rays: resultExtensions?.ray ? [resultExtensions.ray] : undefined,
            requestData: {
              ...(resultExtensions?.LoggerLinkData || {
                start_timestamp: Date.now(),
                duration_ms: 0,
              }),
              url: window.location.href,
            },
          }),
        })
      }
      if (data) {
        setBasketId(
          data.addProductToBasketWithSubscriptionContract
            .addProductToBasketWithSubscriptionContract.id,
        )
        presentAddedToBasketModal?.current(
          String(props.selectedVariant?.sku),
          selectedQuantity,
        )
      }
    },
  })

  const [addToBasketPersonalisedProducts] = useMutation<
    {
      addPersonalisedProductToBasket: AddBasketPersonalisedProduct
    },
    MutationAddPersonalisedProductToBasketArgs
  >(ADD_TO_BASKET_PERSONALISED_PRODUCTS, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      logger.warn(
        `[AddToBasketWithPersonalisedProducts]: Failed to add to basket ${error.message}`,
      )
    },
    onCompleted: (data) => {
      if (data) {
        setBasketId(
          data.addPersonalisedProductToBasket.addPersonalisedProductToBasket.id,
        )
        presentAddedToBasketModal?.current(
          String(props.selectedVariant?.sku),
          selectedQuantity,
        )
      }
    },
  })

  const addPersonalisedProductToBasket = () => {
    if (Object.values(personalisationData).includes('')) {
      return
    }
    setVariantSku(props.selectedVariant?.sku)
    setPersonalisationFields(props.selectedVariant?.personalisationFields)

    if (isPersonalisationActive) {
      window.location.href = personalisationUrl
    } else {
      addToBasketPersonalisedProducts({
        variables: {
          basketId: basket?.id,
          sku: props.selectedVariant?.sku.toString(),
          quantity: selectedQuantity,
          settings: {
            currency: currency || ('' as Currency),
            shippingDestination: shippingDestination || ('' as Country),
          },
          personalisationValues: {
            fieldSubmissionList: Object.keys(personalisationData).map(
              (key) => ({
                name: key,
                value: personalisationData[key],
              }),
            ),
          },
        },
      })
      isAddToBasketSticky && onStickyAddToBasketClick()
    }
  }

  const getSelectedContractPrice = (
    contracts: SubscriptionContract[] | undefined,
    selectedId: string,
    defaultPrice?: string,
  ) => {
    return (
      contracts?.find((contract) => contract.id === selectedId)?.initialPrice
        ?.price?.displayValue ||
      defaultPrice ||
      'N/A'
    )
  }

  const selectedContractDiscountedPrice = getSelectedContractPrice(
    props.selectedVariant?.subscriptionContracts,
    selectedFrequencyId,
    props.selectedVariant?.price?.price.displayValue,
  )

  const addSubscriptionContractToBasket = () => {
    addToBaskedWithContracts({
      variables: {
        basketId: basket?.id,
        sku: props.selectedVariant?.sku.toString(),
        quantity: selectedQuantity,
        contractId: selectedFrequencyId,
        settings: {
          currency: currency || ('' as Currency),
          shippingDestination: shippingDestination || ('' as Country),
        },
      },
    })
    isAddToBasketSticky && onStickyAddToBasketClick()
  }

  const getSelectedContractDiscountMessage = (
    activeTabIndex: number,
    initialDiscountPercentage: number,
    upsellMessage: string,
  ) => {
    if (activeTabIndex === 0 && initialDiscountPercentage) {
      return i18nText.oneTimePurchaseDiscountText(
        `${initialDiscountPercentage}%`,
      )
    }
    if (activeTabIndex === 1) {
      return upsellMessage
    }
    return null
  }

  const displayPurchaseOptions = !!horizonFeatures?.includes(
    Feature.Subscriptions,
  )

  const checkIfSubscriptionBasketEmpty =
    horizonFeatures?.includes(Feature.SubscribeAndSave) &&
    props.selectedVariant &&
    basket?.items?.length === 0

  const productInBasketWithContract =
    !!basketProductSku && props.activeTabIndex === 0

  const productInBasketWithoutContract =
    basketProductSku === null &&
    props.activeTabIndex === 1 &&
    !checkIfSubscriptionBasketEmpty

  const productHasVariantsWithContracts = props.variants.some(
    (variant) => variant?.subscriptionContracts?.length,
  )

  const wishlistPDPButtonStyle =
    theme.pageTheme?.productOptions.addToWishlistButton.style

  const outOfStockPDPButtonStyle =
    theme.pageTheme.productOptions.addToBasketContainer?.outOfStockButton?.style

  const pricing = {
    price:
      props.activeTabIndex === 1 && selectedContractDiscountedPrice
        ? selectedContractDiscountedPrice
        : theme.pageTheme.productSummary.price?.displayStyle ===
            ProductSummaryPriceDisplayStyle.RANGE &&
          product?.priceRange &&
          !selectedVariant
        ? product?.priceRange
        : selectedVariant?.price?.price.displayValue ||
          product?.variants[0].price?.price.displayValue ||
          '0.00',
    rrp:
      selectedVariant?.price?.rrp.displayValue ||
      selectedVariant?.price?.price.displayValue ||
      '0.00',
  }

  const selectedContractUpsellMessage = props.selectedVariant?.subscriptionContracts?.find(
    (contract) => contract.id === selectedFrequencyId,
  )?.upsellMessage

  const selectedContractInitialDiscountPercentage = props.selectedVariant?.subscriptionContracts?.find(
    (contract) => contract.id === selectedFrequencyId,
  )?.initialDiscountPercentage

  const subscriptionContractDiscountMessage = displayPurchaseOptions
    ? getSelectedContractDiscountMessage(
        props.activeTabIndex,
        selectedContractInitialDiscountPercentage || 0,
        selectedContractUpsellMessage,
      )
    : null

  const ProductOptionsComponent = (
    <ProductOptions
      order={theme.pageTheme.productOptions.order}
      options={props.options[productOptionVariantSku] || []}
      onOptionChange={(option, optionValue) => {
        hasSizeOptions && setDisplayError(false)
        productOptions.onOptionChange(option, optionValue)
        onLinkedSkuOptionChange(option)
      }}
      i18nText={productOptions.i18nText}
      labelCustomComponent={props.labelCustomComponent}
      forceDropDowns={props.forceDropDowns}
      swatchShape={props.swatchShape}
      onColourSwatchClick={onColourSwatchClick}
      onDropdownClick={onDropdownClick}
      selectedOptions={productOptions.selectedOptions ?? props.selectedOptions}
      required={false}
      swatch={{
        show: props.showSwatches
          ? props.showSwatches
          : theme.pageTheme?.productOptions.swatch?.show ?? true,
      }}
      notifyWhenInStockInDropdown={props.notifyWhenInStock}
      dropdownWidth={
        theme?.pageTheme?.productOptions?.addToBasketContainer?.fullWidth
          ? '100%'
          : ''
      }
      maxDropdownHeight="300px"
      customErrorMessage={{
        displayError: !enablePreselectedProductVariant && displayError,
        errorMessage: i18nText.missingSizeError,
      }}
      disableColorDropdown={props.disableColorDropdown}
      setPersonalisationImages={setPersonalisationImages}
    />
  )

  const updateImageData = () => {
    if (!personalisationImages) {
      return null
    }

    const selectedImageData =
      personalisationImages[selectedOptions?.selectedImageSwatch]
    setPersonalisationImagesContext(selectedImageData)

    return selectedImageData
  }

  const getAddProductToBasketHandler = (event) => {
    return horizonFeatures?.includes(Feature.SubscribeAndSave) &&
      props.activeTabIndex === 1 &&
      props.selectedVariant?.subscriptionContracts?.length
      ? addSubscriptionContractToBasket()
      : props.selectedVariant?.personalisationData &&
        activePersonalisationTabIndex === 1
      ? addPersonalisedProductToBasket()
      : enableMobilePDPStickyAddToBasket &&
        addToBasketIsSticky &&
        enableQuickbuyOnlyOnMobile
      ? quickBuyModal(event)
      : addProductToBasket()
  }

  const ClickAndCollectButtonRendererComponents = (isHighEmphasis = false) => {
    return (
      <ClickAndCollectButtonRenderer
        text={
          isHighEmphasis
            ? props.selectedVariant?.isCheckStock
              ? props.i18nText.clickAndCollect
                  .clickAndCollectCheckStoreStockText
              : props.i18nText.clickAndCollect.clickAndCollectButtonText
            : props.i18nText.clickAndCollect.clickAndCollectCheckStoreStockText
        }
        disabled={!props.selectedVariant?.inStock || maxQuantity < 1}
        emphasis={isHighEmphasis ? 'high' : 'low'}
        dataTestId={
          isHighEmphasis
            ? 'click-and-collect-button'
            : 'click-and-collect-info-button'
        }
        showIcon
        presentClickAndCollectModalProps={{
          sku: props.selectedVariant?.sku,
          image: props.selectedVariant?.images?.[0]?.largeProduct || '',
          title: props.selectedVariant?.title,
          isProductPage: true,
          quantity: selectedQuantity,
          externalIdentifier: product?.externalIdentifier,
        }}
      />
    )
  }

  const QuantityAndBasketComponent = (
    <span>
      {showQuantity && (
        <QuantityText
          quantityTextLabelStyle={
            theme.pageTheme.productOptions.quantitySelector?.quantityLabelText
              ?.quantityLabel
          }
        >
          {props.i18nText.quantitySelector.quantityLabel}
        </QuantityText>
      )}
      <AddToBasketContainer
        enableQuantity={props.enableQuantity}
        hasPaddingBottom={showPushNotificationView}
        flexDirection={
          theme?.pageTheme?.productOptions?.addToBasketContainer?.direction
        }
      >
        {showQuantity && (
          <QuantitySelectorContainer
            margin={
              theme?.pageTheme?.productOptions?.quantitySelector?.margin || {
                top: '0',
                right: spacing(3),
                bottom: spacing(3),
                left: '0',
              }
            }
          >
            <QuantitySelector {...quantitySelectorProps} />
          </QuantitySelectorContainer>
        )}

        {props.selectedVariant?.maxPerOrder && quantityInBasket > 0 ? (
          <QuantityPlatformMessage
            type="info"
            text={maxQuantityBasketItemMessageText}
            data-testid="quantity-basket-item"
          />
        ) : null}
        {showSetupPushNotificationText && !hasProductOptions && (
          <OutOfStockText
            data-testid="out-of-stock-message"
            content={notificationOptInText}
          />
        )}
        {enableClickAndCollect ? (
          <FulfilmentMethodIconsRenderer
            {...fulfilmentMethodIconsProps}
            deliveryFulfilmentTextStyle={
              theme?.pageTheme?.productOptions?.deliveryFulfilmentTextStyle
            }
          />
        ) : null}
        {(clickAndCollectAvailable || props.selectedVariant?.isCheckStock) &&
          ClickAndCollectButtonRendererComponents()}
        <StyledButtonContainer
          isFullWidth={showPushNotificationView}
          ref={addToBasketButtonContainerRef}
        >
          {showPushNotificationView && props.selectedVariant ? (
            <PushNotifyWhenInStock
              updateProductVariants={updateProductVariants}
              isOptedInForNotification={
                optedInVariants[props.selectedVariant.sku]
              }
              sku={props.selectedVariant.sku}
            />
          ) : (
            showAddToBasket &&
            homeDeliveryAvailable && (
              <AddToBasketButton
                selectedVariant={props.selectedVariant}
                pricing={pricing}
                priceOnSticky={props.enablePriceOnMobilePDPStickyAddToBasket}
                priceTheme={theme.pageTheme.productSummary.price}
                isSticky={isAddToBasketSticky}
                maxQuantity={maxQuantity}
                selectedQuantity={selectedQuantity}
                isPersonalisedProduct={
                  !!selectedVariant?.personalisationFields?.length
                }
                addToBasketText={getAddToBasketText({
                  inStock: !isInStockCheck,
                  addToBasketButton: props.i18nText.addToBasketButton,
                  preorder: selectedVariant?.product?.preorder,
                  personalisedProduct: !!selectedVariant?.personalisationFields
                    ?.length,
                })}
                saveLabel={i18nText.saveLabel}
                enablePreselectedProductVariant={
                  enablePreselectedProductVariant
                }
                addProductToBasketHandler={getAddProductToBasketHandler}
                disabled={
                  (horizonFeatures?.includes(Feature.Subscriptions) &&
                    productInBasketWithContract) ||
                  (productInBasketWithoutContract &&
                    !!props.selectedVariant?.subscriptionContracts?.length) ||
                  (!hasSizeOptions && !props.selectedVariant?.inStock) ||
                  isPersonalisationEmpty(selectedVariant, personalisationData)
                }
                outOfStockPDPButtonStyle={outOfStockPDPButtonStyle}
                offLabel={i18nText.offLabel}
              />
            )
          )}
          {showAddToBasketWishlist && (
            <WishlistButtonWrapper hasBorderOutline={showPushNotificationView}>
              <WishlistButtonRenderer
                selectedVariant={props.selectedVariant || props.defaultVariant}
                buttonWithoutText={true}
                buttonStyle={wishlistButtonStyle}
                buttonType={WishlistButtonType.SMALL}
                itemCategories={product?.content}
                brandName={product?.brand?.name}
                externalIdentifier={product?.externalIdentifier}
              />
            </WishlistButtonWrapper>
          )}
        </StyledButtonContainer>
        {(clickAndCollectAvailable || props.selectedVariant?.isCheckStock) &&
          ClickAndCollectButtonRendererComponents(true)}
      </AddToBasketContainer>
    </span>
  )

  return (
    <React.Fragment>
      {displayPurchaseOptions &&
        (productHasVariantsWithContracts ||
          product?.subscriptionData?.isSubscription) && (
          <PurchaseOptions
            isSubscription={!!product?.subscriptionData.isSubscription}
            subscribeOptions={
              props.selectedVariant?.subscriptionData?.subscribeOptions
            }
            subscriptionPaymentType={
              props.selectedVariant?.subscriptionPaymentType
            }
            subscriptionContracts={props.selectedVariant?.subscriptionContracts}
            selectedOptions={
              productOptions.selectedOptions ?? props.selectedOptions
            }
            onOptionChange={(option, optionValue) => {
              productOptions.onOptionChange(option, optionValue)
              onLinkedSkuOptionChange(option)
            }}
            purchaseOptionsStyle={theme.pageTheme.purchaseOptions}
            showSavingsAmountOnSubscriptionCard={
              showSavingsAmountOnSubscriptionCard
            }
            setActiveTabIndex={props.setActiveTabIndex}
            activeTabIndex={props.activeTabIndex}
            selectedFrequencyId={selectedFrequencyId}
            setSelectedFrequencyId={setSelectedFrequencyId}
            enableSubscribeAndSaveInlinePricing={
              props.enableSubscribeAndSaveInlinePricing
            }
            displaysPrices={{
              oneTimePurchaseDisplayPrice:
                props.defaultVariant?.price?.price.displayValue,
              subscriptionDisplayPrice: selectedContractDiscountedPrice,
            }}
          />
        )}
      {selectedVariant?.personalisationFields ? (
        <PersonalisationToggle
          activePersonalisationTabIndex={activePersonalisationTabIndex}
          setActivePersonalisationTabIndex={setActivePersonalisationTabIndex}
          showClassicContent={
            theme.pageTheme.productPersonalisation?.showClassicContent
          }
          classicContent={
            <React.Fragment>
              {ProductOptionsComponent}
              {QuantityAndBasketComponent}
            </React.Fragment>
          }
          personalisedContent={
            <React.Fragment>
              {ProductOptionsComponent}
              <PersonalisationForm
                selectedVariant={selectedVariant}
                onImageChange={setSelectedOptions}
                imageData={updateImageData()}
                personalisationData={personalisationData}
                selectedImageSwatch={selectedOptions?.selectedImageSwatch}
                personalisationDataCallback={(data: PersonalisationData) => {
                  data && setPersonalisationData(data)
                }}
              />
              {QuantityAndBasketComponent}
            </React.Fragment>
          }
        />
      ) : (
        !product?.subscriptionData?.isSubscription && ProductOptionsComponent
      )}
      {showSetupPushNotificationText && hasProductOptions && (
        <OutOfStockText
          data-testid="out-of-stock-message"
          content={notificationOptInText}
        />
      )}
      {displayPriceAboveQuantity && selectedVariant && (
        <React.Fragment>
          <FreeDeliveryIndicator />
          <PriceContainer reduceBottomMargin={papMessageTopPosition}>
            <Price
              priceTheme={{
                price: theme.pageTheme.productSummary.price.price,
                rrp: theme.pageTheme.productSummary.price.rrp,
                savings: theme.pageTheme.productSummary.price.savings,
                priceWithRRP: theme.pageTheme.productSummary.price.priceWithRRP,
              }}
              priceDirection={theme.pageTheme.productSummary.price.direction}
              displayRRPText={
                theme.pageTheme.productSummary.price.displayRRPText
              }
              pricing={pricing}
              discountMessageProps={{
                text: subscriptionContractDiscountMessage,
                textStyle:
                  theme.pageTheme.productSummary.price.discountMessage
                    ?.textStyle,
              }}
              i18nText={{
                rrpLabel: i18nText.rrpLabel,
                saveLabel: i18nText.saveLabel,
                offLabel: i18nText.offLabel,
              }}
            />
          </PriceContainer>
          <Separator withSpacing />
        </React.Fragment>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!selectedVariant?.personalisationFields && QuantityAndBasketComponent}
        {productInBasketWithContract && (
          <SubscriptionInfoMessage
            text={i18nText.productWithSubscriptionContracts}
            type="info"
          />
        )}
        {productInBasketWithoutContract &&
          !!props.selectedVariant?.subscriptionContracts?.length && (
            <SubscriptionInfoMessage
              text={i18nText.alreadyInBasketAsSubscription}
              type="info"
            />
          )}
      </div>
      {!showAddToBasketWishlist && (
        <WishlistButtonWrapper hasBorderOutline={showPushNotificationView}>
          <WishlistButtonRenderer
            selectedVariant={props.selectedVariant || props.defaultVariant}
            buttonStyle={wishlistButtonStyle}
            buttonType={WishlistButtonType.TEXT}
            wishlistPDPButtonStyle={wishlistPDPButtonStyle}
            itemCategories={product?.content}
            brandName={product?.brand?.name}
            externalIdentifier={product?.externalIdentifier}
          />
        </WishlistButtonWrapper>
      )}
    </React.Fragment>
  )
}
